/**
 * 처방 검수 — 의약품명 검색 다이얼로그.
 * 레거시 DrugSearchSelectModal: 빈 결과 시 추천 검색어 제공.
 */
import { useDrugSearchByName } from '@/entities/drug/hooks/useDrugs';
import type { DrugCodeSearchResponse } from '@/entities/drug/api/drug.api';
import { buildEmptyResultSearchSuggestions } from '@/pages/prescriptions/drugSearchKeywords';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { handleApiError, useToast } from '@/shared/utils/toast';
import React, { useEffect, useState } from 'react';
import { FilterRow } from '@/shared/components/ui/row';

interface PrescriptionDrugNameSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refYm: string;
  initialKeyword?: string;
  /** 추천어 생성용 원문 의약품명 (레거시 draftDrugName) */
  draftDrugName?: string;
  onSelect: (drug: DrugCodeSearchResponse) => void;
}

export const PrescriptionDrugNameSearchDialog: React.FC<
  PrescriptionDrugNameSearchDialogProps
> = ({
  open,
  onOpenChange,
  refYm,
  initialKeyword = '',
  draftDrugName = '',
  onSelect,
}) => {
  const toast = useToast();
  const searchByName = useDrugSearchByName();
  const [keyword, setKeyword] = useState(initialKeyword);
  const [results, setResults] = useState<DrugCodeSearchResponse[]>([]);
  const [searched, setSearched] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setKeyword(initialKeyword);
    setResults([]);
    setSearched(false);
    setSuggestions([]);

    // 레거시: 모달 오픈 시 초기 키워드로 자동 검색
    if (initialKeyword.trim()) {
      void (async () => {
        try {
          const rows = await searchByName.mutateAsync({
            drugName: initialKeyword.trim(),
            refYm,
          });
          setResults(rows);
          setSearched(true);
          if (rows.length === 0) {
            setSuggestions(
              buildEmptyResultSearchSuggestions(
                initialKeyword,
                draftDrugName || initialKeyword,
              ),
            );
          }
        } catch (err) {
          handleApiError(err, toast);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열릴 때 1회
  }, [open, initialKeyword, draftDrugName, refYm]);

  const runSearch = async (nextKeyword: string) => {
    const q = nextKeyword.trim();
    if (!q) return;
    try {
      const rows = await searchByName.mutateAsync({
        drugName: q,
        refYm,
      });
      setResults(rows);
      setSearched(true);
      if (rows.length === 0) {
        setSuggestions(
          buildEmptyResultSearchSuggestions(q, draftDrugName || q),
        );
        toast.error('검색 결과가 없습니다.');
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleSearch = () => {
    void runSearch(keyword);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            의약품명 검색
            {draftDrugName ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                (원래 의약품명: {draftDrugName})
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>
        <FilterRow as="div" gap={8}>
          <div className="flex-1 space-y-1">
            <Label htmlFor="drug-name-search">의약품명</Label>
            <Input
              id="drug-name-search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
          </div>
          <Button
            type="button"
            className="ruda-button"
            onClick={handleSearch}
            disabled={!keyword.trim() || searchByName.isPending}
          >
            검색
          </Button>
        </FilterRow>

        {searched && results.length === 0 && suggestions.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">추천 검색어</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((item) => (
                <Button
                  key={item}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setKeyword(item);
                    void runSearch(item);
                  }}
                >
                  {item}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="max-h-64 overflow-y-auto rounded border">
          {!searched ? (
            <p className="p-4 text-sm text-muted-foreground">
              검색 결과가 표시됩니다.
            </p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              검색 결과가 없습니다.
            </p>
          ) : (
            results.map((item) => (
              <button
                key={`${item.drugCode}-${item.pharmaCompanyId}`}
                type="button"
                className="block w-full border-b px-4 py-3 text-left text-sm hover:bg-muted last:border-b-0"
                onClick={() => {
                  onSelect(item);
                  onOpenChange(false);
                }}
              >
                <span className="font-medium">{item.drugCode}</span> —{' '}
                {item.drugName}
                <span className="ml-2 text-muted-foreground">
                  ({item.pharmaCompanyName})
                  {item.price != null ? ` · ₩${item.price.toLocaleString()}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
